const authorRole = {
  FREE: 'free',
  GROWING: 'growing',
  PRO: 'pro',
  BEST_SELLING: 'bestSelling',
  PUBLISHER: 'publisher'
}

export const bsConstants = {
  APP: {
    VERSION: '2.19.14',
    SOURCE: {
      REVIEWER: 'reviewerApp',
    },
    HOME_URLS: {
      PUBLISHER: '/review-campaigns',
      REVIEWER: '/review-copies',
      ADMIN: '/dashboard',
      WEBSITE: '/authors'
    },
    HELP_HERO_STOP_HELP: new Date(2022, 3, 7), // 7th April
    HELP_HERO_MAX_TOURS: 2,
    HELP_HERE_COMPLETE_ON_TOURS: ['TENO5HCzfsS', 'xgfHLNijbxb'],
    LIVE_DATE: new Date(2022, 2, 7), // 7th March
    NEW_CATEGORIES_LIVE_DATE: new Date(2026, 2, 11), // 11th March 2026
    NEW_BOOK_FIELDS_LIVE_DATE: new Date(2026, 3, 21), // 21st April 2026
  },
  I18N: {
    DEFAULT_LOCALE: 'en-US',
    // Read by the SSR render and the browser alike, so it has to be a cookie rather than
    // local storage - the server needs the reader's choice before it renders a single tag.
    COOKIE_NAME: 'bs-locale',
    COOKIE_MAX_AGE_DAYS: 365,
    // The languages the reader side can be displayed in. This mirrors BOOKS.LANGUAGE_FLAGS
    // minus OTHER, which is a bucket rather than a language and so has nothing to translate to.
    // `languageFlag` ties a UI locale back to the book-language filter, so picking a language
    // can also pre-seed the reader's review-language preference.
    LOCALES: [
      { CODE: 'en-US', NATIVE_NAME: 'English', LANGUAGE_FLAG: 1 },
      { CODE: 'es-ES', NATIVE_NAME: 'Español', LANGUAGE_FLAG: 2 },
      { CODE: 'de-DE', NATIVE_NAME: 'Deutsch', LANGUAGE_FLAG: 4 },
      { CODE: 'fr-FR', NATIVE_NAME: 'Français', LANGUAGE_FLAG: 8 },
      { CODE: 'it-IT', NATIVE_NAME: 'Italiano', LANGUAGE_FLAG: 16 },
      { CODE: 'pt-BR', NATIVE_NAME: 'Português', LANGUAGE_FLAG: 32 }
    ]
  },
  USER: {
    ROLES: {
      APPLICATION: {
        STATUS: {
          NONE: 0,
          APPLIED: 1,
          DECLINED: 2,
          APPROVED: 3
        }
      },
      TYPE: {
        AUTHOR: 0,
        REVIEWER: 1,
        ADMIN: 2
      },
      REVIEWER: {
        UNSET: 'unset',
        NEW: 'new',
        VALUED: 'valued',
        TOP: 'top',
        DEV: 'dev'
      },
      AUTHOR: authorRole
    },
    CONSENT_METHOD: {
      NONE: 0,
      PRE_GDPR: 1,
      SIGN_UP_PAGE: 2,
      SOCIAL_POPUP: 3,
      RECONSENT: 4
    },
    EMAILS: {
      STATUS: {
        VERIFIED: 0,
        UNVERIFIED: 1,
        BLOCKED: 2
      },
      SOFT_BOUNCE_CODE: '4096',
      TRANSIENT_BOUNCE_CODE: '2',
      DNS_ERROR: '256',
      // SOFT_BOUNCE_CODE and DNS_ERROR: the bounces that stop our email only once a second one follows
      // within a day, and that a reader can then clear from the "reactivate" banner. The API's send
      // gate, the banner and the banner's endpoint all read this one list. TRANSIENT_BOUNCE_CODE
      // (Postmark's "Message delayed") is left out on purpose: it never stops our email [sc-29373].
      RECOVERABLE_BOUNCE_CODES: ['4096', '256']
    },
    PASSWORDS: {
      MIN_LENGTH: 8,
      MAX_LENGTH: 65
    },
    ROLE_SELECTION: {
      READER: 1,
      AUTHOR: 2,
      PUBLISHER: 3,
      VA: {
        READER: 4,
        AUTHOR: 5
      }
    },
    REVIEWER_APPLICATION: {
      STATUS: {
        NONE: 0,
        APPLIED: 1,
        DECLINED: 2,
        REDACTED: 3,
        APPROVED: 4
      }
    },
    USER_STATUS: {
      NONE: 0,
      PREVENT_ARC_DOWNLOADS: 1,
      SHADOW_BAN: 2,
      LOCKED: 3,
      INELIGIBLE_REASON_CODE: 4,
      SUSPECTED_SPAM: 5,
      PREVENT_AMAZON_REVIEWS: 6
    },
    USER_STATUS_KEYS: {
      [0]: 'none',
      [1]: 'preventArcDownloads', // reader
      [2]: 'shadowBan', // global
      [3]: 'locked', // global
      [4]: 'ineligibleReasonCode',
      [5]: 'suspectedSpam',
      [6]: 'preventAmazonReviews' // reader
    },
    REPORT_TYPE: {
      NONE: 0,
      REVIEW: 1,
      CANCELLATION: 2,
      MESSAGE: 3,
      GENERIC: 4,
      SYSTEM: 5
    },
    REPORT_TYPE_KEYS: {
      [0]: 'none',
      [1]: 'review',
      [2]: 'cancellation',
      [3]: 'message',
      [4]: 'generic',
      [5]: 'system'
    },
    BREVO_LISTS: {
      NONE: 0,
      ALL: 1,
      LIMITED: 2
    }
  },
  ARC_TEAMS: {
    // The real capacity of the columns these fields are written to, rather than product limits, so
    // nothing already saved can fall foul of them. VarChar caps CHARACTERS while TEXT caps BYTES —
    // hence the differing suffixes. [sc-26648]
    NAME: {
      MAX_CHARS: 191
    },
    DESCRIPTION: {
      MAX_BYTES: 65535
    },
    AUTO_ACTION: {
      EMAIL: {
        MAX_CHARS: 191
      },
      TYPE: {
        APPROVE: 1,
        VIP: 2,
        BLOCK: 3
      }
    },
    MEMBERS: {
      STATUS_LOG: {
        TYPE: {
          LEFT: 0,
          CANCELLED: 1,
          REQUESTED_JOIN: 2
        }
      },
      TYPE: {
        REGULAR: 1,
        VIP: 2,
        NONE: 3 // Not a member
      },
      STATUS: {
        AWAITING: 1,
        ACTIVE: 2,
        BLOCKED: 4,
        REJECTED: 5,
        INVITED: 6,
        ACCEPTED_INVITE: 7,
        NOT_MEMBER: 8
      },
      STATUS_REASON_TYPE: {
        NONE: 0,
        GENERIC: 1,
        LIED: 2,
        CANCELLED: 3,
        NO_INTEREST: 4,
        LOW_RATING: 5,
        HARASSMENT: 6,
        OTHER: 7
      },
      BLOCK_STATUS_REASON_KEYS: {
        [0]: 'generic',
        [1]: 'lied',
        [2]: 'cancelled',
        [3]: 'notInterested',
        [4]: 'lowRating',
        [5]: 'lowRatingHint',
        [6]: 'harassment',
        [7]: 'other'
      }
    },
    DISCLAIMERS: {
      FREE_VIA_BS: 1,
      FREE: 2,
      VIA_ARC: 3
    },
    REVIEW_USER: {
      FILTER: {
        ALL_BS: -1,
        ALL_TEAM: -2
      }
    }
  },
  ARCS: {
    PIRATE: {
      STATE: {
        REPORTED: 0,
        HANDLED: 1
      }
    },
    FILTER_GROUP: {
      ALL: 1,
      AUTHOR_TEAM: 2,
      SAVED: 3
    },
    RELEASE_DATE_TYPE: {
      UNRELEASED: 1,
      RELEASED: 2
    },
    STATUS: {
      NONE: 0,
      ACTIVE: 1,
      SCHEDULED: 2,
      FINISHED: 3
    },
    STATUS_KEYS: {
      [0]: 'draft',
      [1]: 'active',
      [2]: 'scheduled',
      [3]: 'finished'
    },
    // The most campaigns an author can have scheduled at once, on every plan. An account already over it keeps the
    // ones it has, but can't schedule another until it is back under [sc-29193]. The messages saying so name the
    // number too, in every locale.
    MAX_SCHEDULED: 5,
    // An endless audiobook campaign whose every code is taken ends by itself once it has been left alone this many
    // days: no reader claiming, no author saving it, counted from when it went live at the earliest [sc-29306]. The
    // notification telling the author names the number too.
    ENDLESS_AUDIOBOOK_IDLE_DAYS: 30,
    CLAIMS: {
      GROUP_COUNT: {
        ACTIVE: 'active',
        DUE: 'due',
        DELAYED: 'delayed',
        PENDING: 'pending',
        COMPLETE: 'complete',
        CANCELLED: 'cancelled'
      },
      FILTER_GROUP: {
        NONE: 0,
        ACTIVE: 1,
        DUE: 2,
        DELAYED: 3,
        PENDING_PUBLISH: 4,
        COMPLETE: 5,
        CANCELLED: 6
      },
      INELIGIBLE_REASONS: {
        NONE: 0,
        DOWNLOAD_LOCK: 1,
        TOO_MANY_DUE_REVIEWS: 2,
        ARC_DUE_FOR_TOO_LONG: 3,
        WEEKLY_CLAIM_LIMIT_REACHED: 4,
        TOP_REVIEWERS_ONLY: 5,
        PRIVATE_ARC: 6,
        SERIES_READING_REQUIRED: 7,
        OUTSTANDING_REVIEW_FOR_THIS_TEAM: 8,
        STORE_REQUIREMENTS_NOT_MET: 9,
        ALREADY_REVIEWED: 10,
        NO_COPIES_LEFT: 11,
        EMAIL_NOT_VERIFIED: 12,
        NOT_PAID_ACCOUNT: 13,
        TOO_MANY_WITHDRAWALS: 14
      },
      INELIGIBLE_REASON_KEYS: {
        [0]: 'none',
        [1]: 'downloadLock',
        [2]: 'tooManyDueReviews',
        [3]: 'arcDueForTooLong',
        [4]: 'weeklyClaimLimitReached',
        [5]: 'topReviewersOnly',
        [6]: 'privateArc',
        [7]: 'seriesReadingRequired',
        [8]: 'outstandingReviewForThisTeam',
        [9]: 'storeRequirementsNotMet',
        [10]: 'alreadyReviewed',
        [11]: 'noCopiesLeft',
        [12]: 'emailNotVerified',
        [13]: 'notPaidAccount',
        [14]: 'tooManyWithdrawals'
      },
      STATUS: {
        CLAIMED: 0, // When the user has claimed the ARC
        CANCELLED: 1, // When the user cancels their claim - with or without reviews
        COMPLETE: 2, // Claim met all ARC requirements and is done
        IN_PROGRESS: 3 // User has added BS review and it's now "in progress",
      },
      REVIEWER_STATUS: {
        ACTIVE: 0,
        DUE: 1,
        DELAYED: 2,
        PENDING_PUBLISHER: 3,
        COMPLETE: 4,
        WITHDRAWN: 5
      },
      STATE: {
        NEW: 0,
        READ: 1,
        NEW_ACTIVITY: 2,
        NONE: 3
      },
      STATUS_KEYS: {
        [0]: 'claimed',
        [1]: 'cancelled',
        [2]: 'complete',
        [3]: 'inProgress'
      },
      CANCELLATION_TYPE: {
        POORLY_EDITED: 0,
        STORY: 1,
        PERSONAL: 2,
        BAD_DESCRIPTION: 3,
        BAD_FIT: 4,
        COULD_NOT_REVIEW: 5,
        OTHER: 6
      },
      CANCELLATION_TYPE_KEYS: {
        [0]: 'poorlyEdited',
        [1]: 'story',
        [2]: 'personal',
        [3]: 'badDescription',
        [4]: 'badFit',
        [5]: 'couldNotReview',
        [6]: 'other',
      },
      DOWNLOAD_TYPE: {
        NONE: 0,
        KINDLE: 1,
        EMAIL: 2,
        DOWNLOAD: 3,
        AUDIO_BOOK: 4,
        READ_HERE: 5
      },
      FILE_TYPE: {
        NONE: 0,
        EPUB: 1,
        PDF: 2
      }
    },
    REVIEWS: {
      FILTER: {
        ALL_BOOKS: -1,
        ALL_PEN_NAMES: -1
      },
      AUDIO_FLAGS: [256, 512, 1024, 2048, 4096, 8192, 16384, 32768, 65536, 262144],
      // don't add authors direct to ALL_FLAGS
      ALL_FLAGS: 458751,
      FLAGS: {
        NONE: 0,
        AMAZON: 1,
        GOOGLE: 2,
        GOOD_READS: 4,
        KOBO: 8,
        BOOK_BUB: 16,
        ITUNES: 32,
        BARNES: 64,
        SMASH_WORDS: 128,
        AUDIBLE_US: 256,
        AUDIBLE_UK: 512,
        AUDIBLE: 768, // both US and UK combined
        AUDIO_BOOK_BUB: 1024,
        AUDIO_KOBO: 2048,
        AUDIO_GOOGLE: 4096,
        AUDIO_ITUNES: 8192,
        AUDIO_BARNES: 16384,
        AUDIO_GOODREADS: 32768,
        AUDIO_AUTHORS_DIRECT: 65536,
        STORY_GRAPH: 131072,
        AUDIO_STORY_GRAPH: 262144,
      },
      FLAG_BOOK_KEYS: { // This allows quick look ups on a book based on the flags above.
        NONE: '',
        AMAZON: 'linkAmazonAsin',
        GOOGLE: 'linkGoogle',
        GOOD_READS: 'linkGoodreads',
        KOBO: 'linkKobo',
        BOOK_BUB: 'linkBookbub',
        ITUNES: 'linkITunes',
        BARNES: 'linkBarnes',
        SMASH_WORDS: 'linkSmashwords',
        AUDIBLE_US: 'linkAudibleUs',
        AUDIBLE_UK: 'linkAudibleUk',
        AUDIO_BOOK_BUB: 'linkBookbubAudio',
        AUDIO_KOBO: 'linkKoboAudio',
        AUDIO_GOOGLE: 'linkGoogleAudio',
        AUDIO_ITUNES: 'linkITunesAudio',
        AUDIO_BARNES: 'linkBarnesAudio',
        AUDIO_GOODREADS: 'linkGoodreadsAudio',
        AUDIO_AUTHORS_DIRECT: 'linkAuthorsDirect', // not used
        STORY_GRAPH: 'linkStoryGraph',
        AUDIO_STORY_GRAPH: 'linkStoryGraphAudio'
      },
      FLAG_BOOK_KEYS_SITE: { // This allows quick look ups on a book based on the flags above.
        [0]: '',
        [1]: 'linkAmazonAsin',
        [2]: 'linkGoogle',
        [4]: 'linkGoodreads',
        [8]: 'linkKobo',
        [16]: 'linkBookbub',
        [32]: 'linkITunes',
        [64]: 'linkBarnes',
        [128]: 'linkSmashwords',
        [256]: 'linkAudibleUs',
        [512]: 'linkAudibleUk',
        [1024]: 'linkBookbubAudio',
        [2048]: 'linkKoboAudio',
        [4096]: 'linkGoogleAudio',
        [8192]: 'linkITunesAudio',
        [16384]: 'linkBarnesAudio',
        [32768]: 'linkGoodreadsAudio',
        [65536]: 'linkAuthorsDirect', // not used
        [131072]: 'linkStoryGraph',
        [262144]: 'linkStoryGraphAudio'
      },
      FLAG_DESCRIPTIONS: {
        [0]: 'None',
        [1]: 'Amazon',
        [2]: 'Google Play',
        [4]: 'Goodreads',
        [8]: 'Kobo',
        [16]: 'Bookbub',
        [32]: 'Apple Books',
        [64]: 'Barnes & Noble',
        [128]: 'Smashwords',
        [256]: 'Audible US', // Audio
        [512]: 'Audible UK', // Audio
        [768]: 'Audible',
        [1024]: 'Bookbub (Audio)', // Audio
        [2048]: 'Kobo (Audio)', // Audio
        [4096]: 'Google (Audio)', // Audio
        [8192]: 'Apple Books (Audio)', // Audio
        [16384]: 'Barnes & Noble (Audio)', // Audio
        [32768]: 'Goodreads (Audio)', // Audio
        [65536]: 'Authors Direct', // Audio
        [131072]: 'StoryGraph',
        [262144]: 'StoryGraph (Audio)' // Audio
      },
      FLAG_ICONS: {
        [0]: '',
        [1]: 'amazon',
        [2]: 'google-play',
        [4]: 'goodreads',
        [8]: 'kobo',
        [16]: 'bookbub',
        [32]: 'itunes',
        [64]: 'barnes',
        [128]: 'smashwords',
        [256]: 'audible',
        [512]: 'audible',
        [768]: 'audible',
        [1024]: 'bookbub',
        [2048]: 'kobo',
        [4096]: 'google-play',
        [8192]: 'itunes',
        [16384]: 'barnes',
        [32768]: 'goodreads',
        [65536]: 'authors-direct',
        [131072]: 'storygraph',
        [262144]: 'storygraph'
      },
      SITE: {
        STATUS: {
          NONE: 0,
          COMPLETE: 1,
          DELAYED: 2,
          REJECTED: 8
        },
        STATE: {
          NEW_LINK: 0, // This is default so makes sense to have this as zero
          NONE: 1,
          READ: 2
        },
        // The arc_review_site columns these go into: link VARCHAR(1500), statusReason VARCHAR(1000).
        // The API refuses anything longer before writing and the reviewer app's inputs stop it
        // first, so the database is never asked to store it. [sc-28496]
        LINK_MAX_LENGTH: 1500,
        REASON_MAX_LENGTH: 1000
      }
    },
    TYPE: {
      EBOOK: 1,
      AUDIO_BOOK: 2
    },
    DISCOVERABLE_TYPE: {
      PUBLIC: 1,
      TEAM_ONLY: 2
    },
    PARTICIPATION_TYPE: {
      PUBLIC: 1,
      TOP_REVIEWERS: 2,
      ANYONE_WITH_LINK: 3,
      ON_TEAM: 4
    },
    PARTICIPATION_PREVENTION_TYPE: {
      OUTSTANDING: 1,
      NONE: 2
    },
    SITE_WIDE_PARTICIPATION_PREVENTION_TYPE: {
      NONE: 1,
      LIMITED: 2
    },
    COPY_COUNT_TYPE: {
      ALL: 1,
      LIMITED: 2
    },
    SERIES_LIMIT_TYPE: {
      ANYONE: 1,
      PREVIOUS_REVIEWERS: 2
    },
    NEVER_ENDING_PRIVATE_TYPE: {
      NEVER_ENDING: 1,
      LIMITED: 2
    },
    YOUR_ARCS: {
      SORTING: {
        DUE_DESC: 0,
        DUE_ASC: 1,
        CLAIM_DESC: 2,
        CLAIM_ASC: 3,
        RECENT_ACTIVITY: 4
      }
    },
    ALERT_FLAGS: {
      NONE: 0,
      PEN_NAME_FOLLOWERS: 1,
      TEAM_FOLLOWERS: 2,
      TEAM_MEMBERS: 4
    },
    AUDIOBOOK_VENDOR: {
      NONE: 0,
      AUDIBLE: 1,
      AUTHORS_DIRECT: 2,
      KOBO: 3
    },
    PEN_NAME_FILTER_TYPE: {
      ALL: 0,
      FOUR_STARS_AND_ABOVE: 1,
      THREE_STARS_AND_ABOVE: 2,
      NOT_READ: 3
    },
    SORTING: {
      NEW: 0,
      // "Recommended" — the default feed sort, ranked by QUALITY_SCORE below. It's what
      // logged out visitors and readers who haven't chosen a sort get (sc-27838); NEW is
      // still what the server falls back to when a request carries no sort at all.
      RECOMMENDED: 3,
      // The Saved tab's own sorts (sc-28411), both soonest first. ArcQueryBuilderService
      // only honours them on a saved-only read.
      REVIEW_DUE_SOON: 4,
      ENDING_SOON: 5
    },
    // Sort options we no longer offer (1 = Trending, 2 = Popular). Kept so stored
    // selections and in-flight requests from old clients can still be recognised
    // and coerced to RECOMMENDED instead of silently losing their ordering.
    RETIRED_SORTING: [1, 2],
    // What the Saved tab offers, in menu order: New, Review due soon, Ending soon (sc-28411).
    SAVED_SORTING: [0, 4, 5],
    // The "Recommended" feed sort. Ranks eligible ARCs by claims per PEAK REACH
    // (= max(impressions, views)) relative to their category, penalised for missing
    // page counts and author floods, then decayed by age:
    //   reach        = max(impressions, views)   (externally promoted books must
    //                  justify their views with claims: high views + low claims = low)
    //   engagement   = ((claims + K * catCPI) / (reach + K)) / catCPI   (avg book ~= 1)
    //   pageFactor   = PAGE_PENALTY when page count is null / 1 / 2 / 3, else 1
    //   authorFactor = max(MIN_FACTOR, THRESHOLD / max(freshBookArcs, THRESHOLD))
    //   followFactor = FOLLOW_BOOST.FACTOR while the reader follows the book's pen name or team (or is
    //                  an active member of the team) and the ARC went live in the last FOLLOW_BOOST.DAYS,
    //                  or for as long as the reader has the ARC saved
    //   fitFactor    = CATEGORY_FIT, only when the reader filters by category
    //   score        = engagement * pageFactor * authorFactor * followFactor * fitFactor
    //                  / POWER(ageDays + 2, GRAVITY)
    // CAT_CPI baselines are derived from live data — refresh by re-running
    // documentation/feed-quality-score/phase0-validation.sql (Q10).
    QUALITY_SCORE: {
      K: 50, // Bayesian smoothing strength (reach-worth of prior)
      GRAVITY: 1, // freshness decay applied over ARC age in days
      GLOBAL_CPI: 0.025, // fallback claims/reach baseline for uncategorised categories
      // keyed by TOP-LEVEL category id => non-cancelled claims per feed impression
      CAT_CPI: {
        8: 0.0336, // Romance
        1: 0.0162, // Mystery, Thrillers, Action
        32: 0.0155, // Religion & Spirituality
        23: 0.0114, // Speculative Fiction
        36: 0.0093, // Nonfiction
        48: 0.0086, // LGBTQ+
        19: 0.007, // Fiction
        28: 0.0064 // Teen & Young Readers
      },
      // Page-count quality. book.wordCount actually stores page count; values of
      // null / 1 / 2 / 3 are missing data or the legacy SHORT/NOVELLA/NOVEL enum
      // and get a multiplicative penalty (NOT a "longer is better" signal).
      MIN_REAL_PAGE_COUNT: 4,
      PAGE_PENALTY: 0.8,
      // Author flood penalty. Penalise authors pushing many ARCs for newly
      // published / future-dated books at once (spammy + lower-quality signal),
      // but NOT authors uploading a genuine OLDER backlog (books whose releaseDate
      // is outside RECENT_PUB_DAYS are never counted, so a backlog series is exempt).
      AUTHOR_FLOOD: {
        LIVE_WINDOW_DAYS: 30, // window of the author's ARC go-live dates to inspect ("at once")
        RECENT_PUB_DAYS: 90, // a book is "newly published" if releaseDate is within this window (or future)
        THRESHOLD: 10, // up to this many fresh-book ARCs in the window is fine; the penalty ramps in above it
        MIN_FACTOR: 0.2 // floor for the penalty multiplier (max 80% reduction)
      },
      // A head start for the copies a reader has singled out [sc-28740]: books from pen names and teams
      // they follow, or a team they belong to, for their first DAYS live - so a reader who hears about a
      // launch elsewhere finds it near the top - and copies they saved, for as long as they stay saved,
      // as a reminder. FACTOR applies once even when both hold. It multiplies the score rather than
      // pinning the book, and the author flood penalty still applies on top.
      FOLLOW_BOOST: {
        FACTOR: 2,
        DAYS: 7 // counted from the ARC's live date; saved copies have no limit
      },
      // How well a book fits the categories its author picked, scored 0-100 by AI from the blurb
      // (book_category_fit, sc-28740). It only applies when the reader filters by category: the book's
      // best score among the reader's picks decides. NEUTRAL_FROM and up leaves the book where it is
      // (no bonus); lower scores scale the multiplier down to MIN_FACTOR at 0. Unscored books are neutral.
      CATEGORY_FIT: {
        NEUTRAL_FROM: 70,
        MIN_FACTOR: 0.2
      }
    }
  },
  PEN_NAME: {
    TYPE: {
      MY_BOOK: 1,
      PUBLISHING: 2,
      TYPE_CLIENT: 3
    },
    FILTER_GROUP: {
      ALL: 0,
      FOLLOWING: 1
    },
    BIO: {
      // pen_name.bio is MySQL TEXT, which is a hard 65,535 BYTE ceiling. It's the real capacity of
      // the column rather than a product limit, so nothing already saved can fall foul of it. [sc-26988]
      MAX_BYTES: 65535
    }
  },
  NOTIFICATIONS: {
    UNSUB_PREFERENCE_KEYS: {
      1: 'alertNewArc',
      2: 'alertTimeToPublish',
      3: 'alertReviewDueIn2Days',
      4: 'alertReadyToReview',
      5: 'alertFinalFollowUp',
      6: 'alertDelayExpiring',
      7: 'alertDelayExpired',
      8: 'newArcParticipant',
      9: 'newReview',
      10: 'arcCancellation',
      11: 'reviewRejected',
      12: 'newExternalReview',
      13: 'freeText',
      14: 'invitedToTeam',
      15: 'arcCannotActivate',
      16: 'alertNewTeamApplicant',
      17: 'alertBookFileUpdated',
      18: 'alertSavedArcEndingOrLowCopies',
      19: 'alertSavedArcEndingOrLowCopies',
      20: 'alertNewArcDigest',
      101: 'alertTimeToPublish',
      102: 'dailyUpdatesOnFollowCount',
      200: 'alertReviewDueIn2Days'
    },
    FLAGS: {
      EMAIL: 1,
      APP: 2,
      MOBILE_APP: 4
    },
    STATUS: {
      AWAITING: 1,
      COMPLETE: 2,
      CANCELLED: 3,
      FAILED: 4,
      SENT: 5,
      DELIVERED: 6,
      READ: 7,
      OPTED_OUT: 8,
      TO_BE_GENERATED: 9
    },
    STATE: {
      UNREAD: 0,
      READ: 1
    },
    NOTIFIERS: {
      NEW_ARC: {
        CODE: 'newArcAlert',
        TEMPLATE_ID: 'new-arc-alert'
      },
      ARC_DIGEST: {
        CODE: 'arcDigest',
        TEMPLATE_ID: 'new-arc-digest'
      },
      TIME_TO_PUBLISH: {
        CODE: 'timeToPublish',
        TEMPLATE_ID: 'time-to-publish'
      },
      REVIEW_DUE_SOON: {
        CODE: 'reviewsDueSoon',
        TEMPLATE_ID: 'reviews-due-soon'
      },
      READY_TO_REVIEW: {
        CODE: 'readyToReview',
        TEMPLATE_ID: 'ready-to-review'
      },
      FINAL_FOLLOW_UP: {
        CODE: 'finalFollowUp',
        TEMPLATE_ID: 'final-follow-up'
      },
      DELAY_EXPIRING: {
        CODE: 'delayExpiring',
        TEMPLATE_ID: 'delay-expiring'
      },
      DELAY_EXPIRED: {
        CODE: 'delayExpired',
        TEMPLATE_ID: 'delay-expired'
      },
      NEW_ARC_PARTICIPANT: {
        CODE: 'newArcParticipant',
        TEMPLATE_ID: 'new-arc-participant'
      },
      NEW_REVIEW: {
        CODE: 'newReview',
        TEMPLATE_ID: 'new-review'
      },
      ARC_CANCELLATION: {
        CODE: 'arcCancellation',
        TEMPLATE_ID: 'arc-cancellation'
      },
      REVIEW_REJECTED: {
        CODE: 'reviewRejected',
        TEMPLATE_ID: 'review-rejected'
      },
      NEW_EXTERNAL_REVIEW: {
        CODE: 'newExternalReview',
        TEMPLATE_ID: 'new-external-review'
      },
      FREE_TEXT: {
        CODE: 'freeText',
        TEMPLATE_ID: 'free-text-notifier'
      },
      INVITED_TO_TEAM: {
        CODE: 'invitedToTeam',
        TEMPLATE_ID: 'invited-to-team'
      },
      ARC_CANNOT_ACTIVATE: {
        CODE: 'arcCannotActivate',
        TEMPLATE_ID: 'arc-cannot-activate'
      },
      NEW_TEAM_APPLICANT: {
        CODE: 'newTeamApplicant',
        TEMPLATE_ID: 'new-team-applicant'
      },
      BOOK_FILE_UPDATED: {
        CODE: 'bookFileUpdated',
        TEMPLATE_ID: 'book-file-updated'
      },
      SAVED_ARC_ENDING_SOON: {
        CODE: 'savedArcEndingSoon',
        TEMPLATE_ID: 'saved-arc-ending-soon'
      },
      SAVED_ARC_LOW_COPIES: {
        CODE: 'savedArcLowCopies',
        TEMPLATE_ID: 'saved-arc-low-copies'
      }
    },
    AUTHOR_NOTIFIERS: [
      'timeToPublish',
      'newArcParticipant',
      'reviewRejected',
      'newReview',
      'newExternalReview',
      'arcCancellation',
      'arcCannotActivate',
      'newTeamApplicant'
    ]
  },
  MESSAGES: {
    STATE: {
      NEW: 0,
      READ: 1,
      BLOCKED: 2,
      DELETED: 3,
      FLAGGED: 4
    }
  },
  UPLOADS: {
    // nginx's `client_max_body_size` for booksprout.co — see
    // dev-ops/.../nginx/sites-enabled/booksprout.co.conf, and keep the two in step. Anything larger
    // is refused at the edge with a 413 whose body is an HTML error page, so the browser has to
    // refuse it first if the author is to be told anything useful. Held in MB because the message
    // shown to them names the figure. [sc-27747]
    MAX_SIZE_MB: 500
  },
  BOOKS: {
    // The capacity of the columns these fields are written to. VarChar caps CHARACTERS while TEXT
    // caps BYTES — hence the differing suffixes. TITLE and SERIES_NAME sit one short of their
    // column because the API already clamps both to 190 [sc-27467]; validating against the same
    // figure means the author is told rather than quietly losing a character. [sc-27271]
    TITLE: {
      MAX_CHARS: 190
    },
    SERIES_NAME: {
      MAX_CHARS: 190
    },
    PEN_NAME: {
      MAX_CHARS: 191
    },
    BOOK_COVER: {
      MAX_CHARS: 191
    },
    APPLE_BOOKS_CODE: {
      MAX_CHARS: 191
    },
    KEYWORDS: {
      MAX_CHARS: 500
    },
    DESCRIPTION: {
      MAX_BYTES: 65535
    },
    AUDIENCE_TYPE: {
      GENERAL: 1,
      TAME: 2,
      MATURE: 3,
      EXPLICIT: 4
    },
    AUDIENCE_TYPE_KEYS: {
      1: 'general',
      2: 'tame',
      3: 'mature',
      4: 'explicit'
    },
    FILE_UPDATE_REASON: {
      UPLOADED_WRONG_FILE: 0,
      CHANGED_SIGNIFICANT: 1,
      CHANGED_SMALL: 2,
      FIXED_TYPOS: 3
    },
    FILE_UPDATE_REASON_KEYS: {
      [0]: 'uploadedWrongFile',
      [1]: 'changedSignificant',
      [2]: 'changedSmall',
      [3]: 'fixedTypos'
    },
    LANGUAGE_KEYS: {
      1: 'english',
      2: 'spanish',
      4: 'german',
      8: 'french',
      16: 'italian',
      32: 'portuguese',
      64: 'other'
    },
    LANGUAGE_FLAGS: {
      ALL_FLAGS: 127,
      FLAGS: {
        NONE: 0,
        ENGLISH: 1,
        SPANISH: 2,
        GERMAN: 4,
        FRENCH: 8,
        ITALIAN: 16,
        PORTUGUESE: 32,
        OTHER: 64
      }
    },
    WORD_COUNT: {
      SHORT: 1,
      NOVELLA: 2,
      NOVEL: 3
    },
    WORD_COUNT_RANGE: {
      MIN: 0,
      MAX: 2000
    },
    SOURCE: {
      BS: 0,
      IMPORTED: 1
    },
    FILTER: {
      ALPHA: 0,
      DATE_CREATED: 1,
      LAST_ARC: 2,
      REVIEWS_ASC: 3,
      REVIEWS_DESC: 4
    },
    AWAITING_BOOK_FILES: {
      NOT_SET: 0,
      NOT_ALLOWED: 1,
      ALLOW_EPUB: 2,
      ALLOW_EPUB_PREVIEW: 4,
      ALLOW_PDF: 8,
      ALLOW_ALL: 14
    }
  },
  AUTH: {
    LOGIN_TYPE: {
      DEFAULT: 0,
      FACEBOOK: 1,
      AMAZON: 2,
      APPLE: 3
    }
  },
  SUBSCRIPTIONS: {
    DISCOUNTS: {
      END_DATE: new Date(2022, 2, 22), // (month is zero indexed)
      MONTH: 25,
      YEAR: 35
    },
    PLANS: {
      FREE: {
        PRICES: {
          MONTH: 0,
          YEAR: 0
        },
        KEY: authorRole.FREE
      },
      GROWING: {
        PRICES: {
          MONTH: 9,
          YEAR: 90
        },
        KEY: authorRole.GROWING
      },
      PRO: {
        PRICES: {
          MONTH: 19,
          YEAR: 190
        },
        KEY: authorRole.PRO
      },
      BEST_SELLER: {
        PRICES: {
          MONTH: 29,
          YEAR: 290
        },
        KEY: authorRole.BEST_SELLING
      },
      PUBLISHER: {
        PRICES: {
          MONTH: 59,
          YEAR: 590
        },
        KEY: authorRole.PUBLISHER,
        // Staff set each account's limit on live review campaigns (user.maxRunningArcsOverride); until
        // they do, it runs this many at once [sc-29038]
        INITIAL_MAX_RUNNING_ARCS: 10,
        // The longest answers the checkout's Business Info step, and staff's notes on it, are stored
        // with [sc-29190]
        APPLICATION: {
          WEBSITE_MAX_LENGTH: 500,
          INFORMATION_MAX_LENGTH: 2000,
          NOTE_MAX_LENGTH: 2000
        }
      }
    },
    CANCELLATION_TYPE: {
      TEMPORARY: 0,
      DIFFICULT_FOR_READERS: 1,
      DIFFICULT_FOR_ME: 2,
      NOT_WORKING: 3,
      LACKING_SUPPORT: 4,
      COST: 5,
      OTHER: 6,
      NOT_ENOUGH_REVIEWS: 7
    },
    CANCELLATION_TYPE_KEYS: {
      0: 'temporary',
      1: 'difficultForReaders',
      2: 'difficultForMe',
      3: 'notWorking',
      4: 'lackingSupport',
      5: 'cost',
      6: 'other',
      7: 'notEnoughReviews'
    },
    // The two headings a cancellation is filed under when it carries none of the reasons above: the
    // author picked none, or Stripe ended the subscription itself (a failed payment, a chargeback).
    // Kept apart from CANCELLATION_TYPE because the cancel dialog offers exactly that list. Negative so
    // a reason added later can never collide with them, and not -1, which the dialog sends for "Please
    // select" and the admin filters use for "every type". [sc-28213]
    CANCELLATION_CATEGORY: {
      NO_REASON: -2,
      STRIPE: -3
    },
    CANCELLATION_CATEGORY_KEYS: {
      '-2': 'noReasonGiven',
      '-3': 'cancelledByStripe'
    },
    // Where a subscription_cancellation row came from. [sc-28213]
    CANCELLATION_SOURCE: {
      // The author's own cancel dialog.
      APP: 1,
      // A Stripe webhook with no in-app cancellation to match: Stripe ended the subscription itself, or
      // someone cancelled it from the Stripe dashboard.
      STRIPE: 2,
      // Copied from the user row when the table was created - the only history there was.
      IMPORTED: 3
    },
    // Why a failed signup's first payment did not go through: Stripe's decline codes in the groups the
    // admin dashboard's Failed subscription signups chart shows. The values are i18n keys. [sc-28213]
    FAILED_SIGNUP_REASON: {
      NOT_ENOUGH_MONEY: 'notEnoughMoney',
      BANK_REFUSED: 'bankRefused',
      NOT_ALLOWED: 'notAllowed',
      TEMPORARY: 'temporary',
      WRONG_DETAILS: 'wrongDetails',
      FRAUD: 'fraud',
      STOPPED_BY_CARDHOLDER: 'stoppedByCardholder',
      // No decline was recorded: the payment never got as far as the bank, or Stripe gave no reason.
      UNKNOWN: 'unknown'
    }
  },
  // Generic product-metric event log (metric_tracker table). Events are written
  // fire-and-forget and analysed offline; see documentation/feed-quality-score.
  METRIC_TRACKER: {
    TYPES: {
      // Reviewer entered the find-review-copies feed (or switched its sort).
      FEED_ENTER: 'feed.enter',
      // Reviewer claimed an ARC; metaData carries claimId/arcId/sortBy/secondsToClaim.
      FEED_CLAIM: 'feed.claim',
      // The four events behind the "why didn't the update-your-readers popup show?" question, which
      // nothing used to record - sc-28487 could only be answered by inferring from arc rows. Together
      // they say, for any book file upload: what the server decided, whether the author actually saw
      // the popup, and what they did with it. OFFERED is written server-side on EVERY add-file (its
      // metaData carries the eligible campaign ids, empty when there were none, so "we never offered
      // it" is recorded just as firmly as "we did"); the other three are written by the dialog.
      BOOK_FILE_UPDATE_OFFERED: 'bookFileUpdate.offered',
      BOOK_FILE_UPDATE_SHOWN: 'bookFileUpdate.shown',
      BOOK_FILE_UPDATE_DISMISSED: 'bookFileUpdate.dismissed',
      BOOK_FILE_UPDATE_SENT: 'bookFileUpdate.sent',
      // Which way a reader asked for their review copy, and on what. Nothing recorded this, so
      // "are they emailing it or downloading it, in a browser or in the app?" could only be put to
      // the reader - and in sc-28415 the reader answered it three different ways across a week,
      // which is most of why that one took as long as it did.
      //
      // metaData carries claimId/arcId/downloadType/fileType/isNativeApp. Written when the reader
      // ASKS, not when delivery succeeds: the failures this exists to catch are silent ones, so an
      // event that only fired on success would be missing in exactly the cases that need it.
      DELIVERY_SELECTED: 'delivery.selected'
    },
    // localStorage key holding { enteredAt, sortBy } for the reviewer's current
    // feed session, used to compute secondsToClaim when a claim happens.
    FEED_SESSION_STORAGE_KEY: 'bsFeedSession'
  }
}
